import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  FileText,
  Scale,
  ShieldCheck,
  Zap,
  MapPin,
} from "lucide-react";
import LeadForm from "@/components/LeadForm";
import FAQAccordion from "@/components/FAQAccordion";
import JsonLd from "@/components/JsonLd";
import useSEO from "@/lib/useSEO";
import {
  softwareApplicationSchema,
  faqPageSchema,
  breadcrumbSchema,
  SITE_URL,
} from "@/lib/schema";
import { CITIES, LEGAL_FAQ, LEGAL_KEYWORDS, BLOG_POSTS } from "@/data/content";

const DRAFT_URL = "https://draftmaster.ai";

export default function Draftmaster() {
  useSEO({
    title:
      "DraftMaster.ai — AI Legal Drafting & Automated Contract Drafting Tool | LexWorkspace",
    description:
      "DraftMaster.ai is the AI-powered legal drafting platform for attorneys, paralegals and corporate legal teams — contracts, motions, briefs, employment and real-estate documents with citation safety and your firm's house style. Serving DC, NYC, LA, Chicago and Houston.",
    keywords:
      "AI legal drafting software, automated contract drafting tool, AI contract drafting tool, automated legal document drafting, AI for law firm document automation, legal brief drafting AI, AI tool for lawyers to draft documents, legal document automation for attorneys",
    url: SITE_URL + "/draftmaster",
    image:
      "https://images.pexels.com/photos/8112126/pexels-photo-8112126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  });
  const legalPosts = BLOG_POSTS.filter((p) => p.product === "draftmaster");

  return (
    <div data-testid="draftmaster-page">
      <JsonLd
        id="draft-software"
        data={softwareApplicationSchema({
          name: "DraftMaster.ai",
          url: DRAFT_URL,
          description:
            "AI-powered legal drafting and document automation platform for attorneys, paralegals and corporate legal teams. Drafts contracts, motions, briefs, employment agreements, real-estate documents and legal correspondence with clause-library control and citation safety.",
          category: "BusinessApplication",
          audience:
            "Attorneys, Paralegals, Corporate Legal Departments, Solo Practitioners",
        })}
      />
      <JsonLd id="draft-faq" data={faqPageSchema(LEGAL_FAQ)} />
      <JsonLd
        id="draft-breadcrumbs"
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Legal AI", url: SITE_URL + "/draftmaster" },
        ])}
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/8112126/pexels-photo-8112126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Lawyer drafting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 lg:py-44 text-white">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs tracking-[0.3em] uppercase text-white/70 mb-6"
          >
            DraftMaster.ai · AI legal drafting platform
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="draft-h1"
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
          >
            AI legal drafting that lawyers{" "}
            <span className="text-[#6EE7B7]">actually trust.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl"
          >
            Purpose-built for attorneys, paralegals and corporate legal teams.
            Draft contracts, motions, briefs, employment agreements,
            real-estate documents and legal correspondence — with your clause
            library, your house style, and an accuracy audit on every output.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <a
              href={DRAFT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="draft-hero-cta-primary"
              className="btn-sharp bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
            >
              Visit draftmaster.ai <ArrowUpRight size={14} />
            </a>
            <a
              href="#capabilities"
              data-testid="draft-hero-cta-secondary"
              className="btn-sharp border border-white text-white hover:bg-white hover:text-black"
            >
              See capabilities
            </a>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section
        id="capabilities"
        className="py-24 lg:py-32 border-b border-black/10"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            What it drafts
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none max-w-3xl mb-16">
            Built for the documents lawyers actually ship.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-black/10">
            {[
              {
                icon: FileText,
                title: "Contracts",
                body: "NDAs, MSAs, SaaS, vendor agreements, employment, real-estate purchase & lease.",
              },
              {
                icon: Scale,
                title: "Litigation",
                body: "Motions, briefs, demand letters, discovery responses, status memos.",
              },
              {
                icon: Zap,
                title: "Correspondence",
                body: "Client emails, opposing counsel letters, internal memos — your house voice.",
              },
              {
                icon: ShieldCheck,
                title: "Compliance",
                body: "Policy memos, regulatory filings, employment handbooks, vendor due diligence.",
              },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  data-testid={`draft-capability-${i}`}
                  className={
                    "p-8 lg:p-10 border-black/10 " +
                    (i < 3 ? "md:border-r border-b md:border-b-0" : "")
                  }
                >
                  <Icon
                    size={28}
                    className="text-[#065F46] mb-6"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-24 lg:py-32 bg-[#ECFDF5] border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-[#065F46] font-semibold mb-4">
              Who it&apos;s for
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              From solos to AmLaw 200.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              {
                t: "Solo attorneys",
                b: "Cut drafting time on engagement letters, NDAs and demand letters from hours to minutes.",
              },
              {
                t: "Small firms",
                b: "Lock in firm-standard clauses across associates without manual review of every draft.",
              },
              {
                t: "Corporate legal",
                b: "High-volume contract intake, vendor agreements and employment paperwork at scale.",
              },
              {
                t: "Litigation teams",
                b: "Brief structure, argument scaffolding, and a verified-citation pipeline.",
              },
              {
                t: "Paralegals",
                b: "First-pass drafts ready for attorney review with citations and accuracy flags attached.",
              },
              {
                t: "Real-estate practices",
                b: "Purchase agreements, leases, escrow letters with jurisdictional clause variants.",
              },
            ].map((it, i) => (
              <div key={i} data-testid={`draft-audience-${i}`} className="border-t border-black/10 pt-6">
                <p className="font-serif-display text-2xl mb-2">{it.t}</p>
                <p className="text-sm text-gray-700 leading-relaxed">{it.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITY LINKS */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
                Legal AI by city
              </p>
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
                Local market context.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-black/10">
            {CITIES.map((c, i) => (
              <Link
                key={c.slug}
                to={`/legal-ai/${c.slug}`}
                data-testid={`draft-city-${c.slug}`}
                className={
                  "p-6 lg:p-8 hover:bg-[#ECFDF5] transition-colors group " +
                  (i < CITIES.length - 1 ? "border-r border-black/10" : "") +
                  (i < 2 ? " border-b lg:border-b-0 border-black/10" : "")
                }
              >
                <MapPin size={18} className="text-[#065F46] mb-4" />
                <p className="font-serif-display text-2xl mb-2 leading-tight">
                  {c.short}
                </p>
                <p className="text-xs text-gray-500">
                  {c.legalStats.firms} firms
                </p>
                <p className="text-xs tracking-widest uppercase font-semibold text-[#065F46] mt-4 group-hover:underline">
                  Open →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-none mb-12">
            Legal AI reading.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {legalPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`draft-blog-${p.slug}`}
                className="group border border-black/10 bg-white hover:shadow-lg transition-shadow"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Legal AI FAQ
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              Common questions.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={LEGAL_FAQ} testIdPrefix="draft-faq" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#065F46] text-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-4">
              Ready to draft faster?
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none">
              Move your drafting onto DraftMaster.
            </h2>
            <p className="text-white/70 mt-6 max-w-md leading-relaxed">
              Get the full platform on draftmaster.ai — or join our list for a
              short evaluation guide tailored to small and mid-sized firms.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={DRAFT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="draft-bottom-cta"
                className="btn-sharp bg-white text-[#065F46] hover:bg-white/90 inline-flex items-center gap-2"
              >
                Open draftmaster.ai <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div>
            <LeadForm
              interest="draftmaster"
              source="draftmaster-page-cta"
              variant="dark"
              outboundUrl={DRAFT_URL}
              testIdPrefix="draft-lead"
              ctaLabel="Send Evaluation Guide"
            />
          </div>
        </div>
      </section>

      {/* KEYWORD CLOUD */}
      <section className="py-16 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
            Topics we cover
          </p>
          <div className="flex flex-wrap gap-2">
            {LEGAL_KEYWORDS.map((k) => (
              <span
                key={k}
                className="text-xs px-3 py-1.5 border border-black/15 text-gray-700"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
